import { Clock, ArrowRightIcon, SparklesIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { InterviewItem } from "@type/interview";
import { formatDuration } from "@util/date";

export interface Props {
    interview: InterviewItem | null;
}

// Per-category background tint for the card header. Keeps the cards
// visually distinct without needing a category icon — the ourteam
// logo stays as the centerpiece.
const CATEGORY_BG: Record<string, string> = {
    Software: "bg-blue-50",
    Data: "bg-green-50",
    Finance: "bg-yellow-50",
    Product: "bg-indigo-50",
    Business: "bg-amber-50",
    Consulting: "bg-amber-50",
    Writing: "bg-stone-50",
    Design: "bg-pink-50",
    Legal: "bg-slate-50",
    Media: "bg-rose-50",
    Engineering: "bg-orange-50",
    Statistics: "bg-yellow-50",
    Marketing: "bg-purple-50",
    Biology: "bg-teal-50",
    Security: "bg-gray-50",
    Blockchain: "bg-cyan-50",
    "Soft Skill": "bg-teal-50",
};

export default function InterviewCard(props: Props) {
    const navigate = useNavigate();
    const interview = props.interview;
    const headerBg = CATEGORY_BG[interview?.category || ""] ?? "bg-gray-50";

    const handleStart = () => {
        if (!interview) return;
        navigate(`/interview/${interview.id}/start`, {
            state: { isPracticeInterview: true },
        });
    };

    // Skeleton variant — preserves the card height so the grid doesn't
    // jump when results land.
    if (!interview) {
        return (
            <div className="rounded-2xl overflow-hidden border border-gray-200 bg-white min-h-[330px] flex flex-col">
                <div className="bg-gray-100 h-36 animate-pulse" />
                <div className="p-5 flex-1 flex flex-col">
                    <div className="h-4 w-16 bg-gray-200 rounded-full mb-3 animate-pulse" />
                    <div className="h-5 w-3/4 bg-gray-200 rounded mb-2 animate-pulse" />
                    <div className="h-4 w-full bg-gray-100 rounded mb-1 animate-pulse" />
                    <div className="h-4 w-5/6 bg-gray-100 rounded mb-6 animate-pulse" />
                    <div className="mt-auto flex items-center justify-between">
                        <div className="h-6 w-16 bg-gray-100 rounded-full animate-pulse" />
                        <div className="h-4 w-20 bg-gray-100 rounded animate-pulse" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={handleStart}
            className="group text-left rounded-2xl overflow-hidden border border-gray-200 bg-white hover:border-blue-400 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 min-h-[330px] flex flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
            {/* Decorative header — category-themed background + the
                ourteam logo so cards stay on-brand. */}
            <div className={`${headerBg} relative h-36 flex items-center justify-center overflow-hidden`}>
                {/* Premium ribbon */}
                {interview.is_premium && (
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-blue-600 to-blue-500 text-white text-[10px] font-semibold uppercase tracking-wide px-2 py-1 shadow-sm">
                        <SparklesIcon className="h-3 w-3" />
                        Pro
                    </span>
                )}
                <div className="h-16 w-16 rounded-full bg-white shadow-sm ring-1 ring-black/5 overflow-hidden flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
                    {/* Fill the circle edge-to-edge — `object-cover`
                        stretches the logo to the container bounds so
                        no transparent border around the PNG is visible. */}
                    <img
                        src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`}
                        alt="ourteam"
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover"
                    />
                </div>
            </div>

            {/* Body */}
            <div className="p-5 flex-1 flex flex-col">
                <h3 className="text-base font-semibold text-gray-900 leading-snug line-clamp-2 mb-1.5">
                    {interview.title}
                </h3>
                <p className="text-sm text-gray-500 line-clamp-2 leading-relaxed">
                    {interview.description}
                </p>

                {/* Footer */}
                <div className="mt-auto pt-4 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1">
                        <Clock className="h-3.5 w-3.5" />
                        {formatDuration(interview.duration || 0)}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 group-hover:gap-2 transition-all">
                        Start
                        <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                </div>
            </div>
        </button>
    );
}
